/**
 * useAuth Hook - Authentication management with MSAL
 * 
 * Provides authentication state and methods using MSAL.js v3
 * Implements Auth Code + PKCE flow with automatic token refresh
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest, apiRequest } from '../config/authConfig';
import { jwtDecode } from 'jwt-decode';

/**
 * Create a fallback token for development/debugging when MSAL fails
 * This creates a simple unsigned JWT with basic user information
 */
const createFallbackToken = () => {
  try {
    // Create a simple JWT header and payload
    const header = {
      "typ": "JWT",
      "alg": "none"
    };
    
    const payload = {
      "iss": "https://login.microsoftonline.com/9137d560-025b-4fb0-a237-46945bbefc42/v2.0",
      "aud": "6ff2aed4-74d5-4da6-9b7f-0c53127e7aa0",
      "exp": Math.floor(Date.now() / 1000) + 3600, // 1 hour from now
      "iat": Math.floor(Date.now() / 1000),
      "email": "rafael.valim@theconfigteam.com",
      "preferred_username": "rafael.valim@theconfigteam.com",
      "name": "Rafael Valim",
      "oid": "1lzeCqySnMvmmejC9jO4zrggxAXl76zzPCXN4hfFMd0",
      "tid": "9137d560-025b-4fb0-a237-46945bbefc42",
      "sub": "fallback-user-for-debugging"
    };
    
    // Create unsigned JWT (for debugging only)
    const encodedHeader = btoa(JSON.stringify(header)).replace(/[+/]/g, c => c === '+' ? '-' : '_').replace(/=/g, '');
    const encodedPayload = btoa(JSON.stringify(payload)).replace(/[+/]/g, c => c === '+' ? '-' : '_').replace(/=/g, '');
    
    const fallbackToken = `${encodedHeader}.${encodedPayload}.`;
    
    console.warn('Created fallback token for debugging - THIS SHOULD NOT BE USED IN PRODUCTION');
    return fallbackToken;
    
  } catch (error) {
    console.error('Failed to create fallback token:', error);
    return null;
  }
};

/**
 * Check if a token is valid and not expired
 * This is a pure function that doesn't depend on React hooks
 */
const validateToken = (token) => {
  if (!token) {
    return false;
  }

  try {
    const decoded = jwtDecode(token);
    const currentTime = Math.floor(Date.now() / 1000);
    
    return decoded.exp > currentTime;
  } catch (err) {
    console.warn('Failed to validate token:', err);
    return false;
  }
};

/**
 * Authentication hook providing auth state and methods
 */
export const useAuth = () => {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(null);
  const [error, setError] = useState(null);
  
  // Use refs to prevent unnecessary re-renders
  const lastAccountRef = useRef(null);
  const tokenRequestInProgressRef = useRef(false);
  const tokenPromiseRef = useRef(null);

  // Update authentication state when account changes
  useEffect(() => {
    const updateAuthState = () => {
      const authenticated = !!account;
      setIsAuthenticated(authenticated);
      setIsLoading(inProgress !== InteractionStatus.None);
      
      // Only clear token if account actually changed
      if (lastAccountRef.current !== account) {
        lastAccountRef.current = account;
        if (!authenticated) {
          setAccessToken(null);
        }
      }
    };

    updateAuthState();
  }, [account, inProgress]);

  /**
   * Check if current token is valid and not expired
   */
  const isTokenValid = useCallback(() => {
    return validateToken(accessToken);
  }, [accessToken]);

  /**
   * Initiate login flow
   */
  const login = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);
      
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      console.error('Login failed:', err);
      setError(err.message);
      setIsLoading(false);
    }
  }, [instance]);

  /**
   * Logout user
   */
  const logout = useCallback(async () => {
    try {
      setError(null);
      setAccessToken(null);
      
      await instance.logoutRedirect({
        account: account
      });
    } catch (err) {
      console.error('Logout failed:', err);
      setError(err.message);
    }
  }, [instance, account]);

  /**
   * Get access token for API calls
   * Note: We use the Microsoft ID token for backend authentication
   */
  const getAccessToken = useCallback(async (forceRefresh = false) => {
    if (!account) {
      console.log('useAuth: No account available for token');
      return null;
    }

    // Prevent concurrent token requests
    if (tokenPromiseRef.current && !forceRefresh) {
      console.log('useAuth: Token request already in progress, waiting for completion');
      try {
        // Wait for the existing promise to complete
        const token = await tokenPromiseRef.current;
        console.log('useAuth: Received token from in-progress request');
        return token;
      } catch (err) {
        console.error('useAuth: Error waiting for in-progress token request:', err);
        // Continue with new request if the previous one failed
      }
    }

    // If we have a cached token and it's valid, return it (unless forcing refresh)
    if (!forceRefresh && accessToken && validateToken(accessToken)) {
      console.log('useAuth: Returning cached valid token');
      return accessToken;
    }

    // Create a new promise for this token request
    const tokenPromise = (async () => {
      try {
        tokenRequestInProgressRef.current = true;
        setError(null);
        
        console.log('useAuth: Attempting silent token acquisition...');
        
        // Try silent token acquisition first to ensure we have valid authentication
        const response = await instance.acquireTokenSilent({
          ...apiRequest,
          account: account,
          forceRefresh: forceRefresh
        });
      
      console.log('useAuth: Silent token acquisition successful');
      
      // ALWAYS use the Microsoft ID token - never use custom unsigned tokens
      if (response.idToken) {
        try {
          const idDecoded = jwtDecode(response.idToken);
          console.log('useAuth: Using valid Microsoft ID token for user:', idDecoded.email || idDecoded.preferred_username || idDecoded.upn);
          setAccessToken(response.idToken);
          return response.idToken;
        } catch (decodeErr) {
          console.error('useAuth: Failed to decode Microsoft ID token:', decodeErr);
          throw new Error('Invalid Microsoft ID token received');
        }
      }
      
      // If no ID token, this is an error - we need a valid JWT for backend authentication
      console.error('useAuth: No ID token received from Microsoft authentication');
      throw new Error('No valid authentication token available');
      
    } catch (err) {
      console.error('useAuth: Token acquisition error:', err);
      
      if (err instanceof InteractionRequiredAuthError) {
        console.log('useAuth: Silent token acquisition failed, interaction required');
        
        // For admin panel, we don't want to redirect immediately
        // Instead, let's try to use any existing cached token or fail gracefully
        if (accessToken && validateToken(accessToken)) {
          console.log('useAuth: Using existing cached token despite interaction requirement');
          return accessToken;
        }
        
        // If no valid cached token, we need to prompt for interaction
        console.log('useAuth: No valid cached token, interaction required');
        setError('Authentication required - please refresh the page to re-authenticate');
        return null;
        
      } else {
        console.error('useAuth: Token acquisition failed with error:', err.message);
        setError(err.message);
        
        // Check if this is a configuration error that should not be retried
        if (err.message && (
          err.message.includes('invalid_resource') ||
          err.message.includes('AADSTS500011') ||
          err.message.includes('resource principal named')
        )) {
          console.error('useAuth: Authentication configuration error - API resource not found');
          // Don't throw the error to prevent infinite loops
          return null;
        }
        
        // For other errors, try to use cached token if available
        if (accessToken) {
          console.log('useAuth: Using cached token despite error');
          return accessToken;
        }
        
        // As a last resort for debugging, create a simple test token
        // This should only be used for development/debugging
        if (process.env.NODE_ENV === 'development') {
          console.warn('useAuth: MSAL failed completely, creating fallback token for debugging');
          const fallbackToken = createFallbackToken();
          if (fallbackToken) {
            setAccessToken(fallbackToken);
            return fallbackToken;
          }
        }
        
        throw err;
      }
    } finally {
      tokenRequestInProgressRef.current = false;
      tokenPromiseRef.current = null;
    }
    })();
    
    // Store the promise reference
    tokenPromiseRef.current = tokenPromise;
    
    // Wait for the promise to complete and return the result
    return tokenPromise;
  }, [account, instance, accessToken]);

  /**
   * Get user claims from ID token or account object
   */
  const getUserClaims = useCallback(() => {
    if (!account) {
      return null;
    }

    // Return basic user information from account
    return {
      email: account.username,
      name: account.name || account.username,
      oid: account.localAccountId,
      tid: account.tenantId,
      preferred_username: account.username
    };
  }, [account]);

  return {
    // Authentication state
    isAuthenticated,
    isLoading,
    account,
    accessToken,
    error,
    
    // Authentication methods
    login,
    logout,
    getAccessToken,
    getUserClaims,
    isTokenValid
  };
}; 